import { FC, useState } from 'react';

import { router, usePage } from '@inertiajs/react';

import { AdBanner } from './AdBanner';
import { FaqBanner } from './FaqBanner';
import { DesktopFilters } from './filters/DesktopFilters';
import { DesktopFiltersLayout } from './filters/DesktopFiltersLayout';
import { MobileFilters } from './filters/MobileFilters';
import { PremiumOffersSection } from './PremiumOffersSection';
import { premiumOffersRoute } from '../../models/routes';
import { Expansion, Offer, Universe } from '../../types';

type PageProps = {
    premiumOffers: Offer[] | null;
    premiumOffersCount: number;
    nextPage: number | null;
    randomSeed: string;
    currentUniverse: Universe;
    currentExpansion: Expansion | null;
    expansions: Expansion[] | null;
    q: Record<string, string>;
};

const PremiumOffersPage: FC = () => {
    const {
        premiumOffers,
        premiumOffersCount,
        nextPage,
        randomSeed,
        currentUniverse,
        currentExpansion,
        expansions,
        q,
    } = usePage<PageProps>().props;
    const [loading, setLoading] = useState(false);

    const premiumOffersFilterUrl = ({
        filters,
        expansionSlug,
    }: {
        filters: Record<string, string>;
        expansionSlug?: string;
    }) => {
        const filtersSlug = Object.entries(filters)
            .filter(([, value]) => value && value !== 'all')
            .map(([key, value]) => `${key}-${value}`)
            .join('/');
        const path = `${premiumOffersRoute(currentUniverse.slug)}${
            filtersSlug ? `/filters/${filtersSlug}` : ''
        }`;

        if (!expansionSlug || expansionSlug === 'all') {
            return path;
        }

        return `${path}?expansion_slug=${encodeURIComponent(expansionSlug)}`;
    };

    const applyFilters = ({
        q: filters,
        expansionSlug,
        preserveState,
    }: {
        q: Record<string, string>;
        expansionSlug?: string;
        preserveState?: boolean;
    }) => {
        router.get(
            premiumOffersFilterUrl({ filters, expansionSlug }),
            {},
            {
                preserveScroll: true,
                preserveState: Boolean(preserveState),
                onStart: () => setLoading(true),
                onFinish: () => setLoading(false),
            },
        );
    };

    const resetFilters = () => {
        router.get(premiumOffersRoute(currentUniverse.slug));
    };

    const loadMore = () => {
        if (!nextPage) return;

        router.get(
            window.location.href,
            { page: nextPage, random_seed: randomSeed },
            {
                preserveScroll: true,
                preserveState: true,
                preserveUrl: true,
                only: ['premiumOffers', 'nextPage'],
                onStart: () => setLoading(true),
                onSuccess: () => setLoading(false),
                onFinish: () => setLoading(false),
            },
        );
    };

    return (
        <main className="flex-1">
            <div className="container px-4 py-6 sm:px-6 lg:px-8">
                <DesktopFiltersLayout
                    sidebar={
                        <DesktopFilters
                            filters={q}
                            onFilterChange={applyFilters}
                            onResetFilters={resetFilters}
                            expansion={currentExpansion}
                            expansions={expansions}
                        />
                    }
                >
                    <PremiumOffersSection
                        offers={premiumOffers}
                        title="Усі преміум пропозиції"
                        description="Каталог усіх активних преміум-пропозицій від продавців CartOn."
                        resultsCount={premiumOffersCount}
                        nextPage={nextPage}
                        loading={loading}
                        onLoadMore={loadMore}
                        sectionClassName="border-0 py-0"
                        contentClassName="px-0 sm:px-0 lg:px-0"
                        withDesktopSidebar
                    />
                </DesktopFiltersLayout>
            </div>

            <MobileFilters
                filters={q}
                onFilterChange={applyFilters}
                expansion={currentExpansion}
            />

            <div className="hidden md:block">
                <AdBanner className="mb-4 sm:mb-6" />
            </div>

            <div className="md:hidden">
                <AdBanner className="mb-4 sm:mb-6" />
            </div>

            <FaqBanner />
        </main>
    );
};

export default PremiumOffersPage;
